import {
  AiApiToolOption,
  AiApiToolSaveRow,
  AiModelOption,
  AiProcessPromptConfig,
  AiProcessPromptSaveRow,
  compareProcessTypes,
  DEFAULT_CLOSE_REMARK_OPTIONS,
  normalizeCloseRemarkOptions,
} from "../../utils/aiDocumentVerification";
import { isOllamaEndpoint } from "./aiProviderPresets";

/** Max output / completion tokens allowed per OpenAI model family. */
export function getModelOutputTokenCap(modelId: string): number {
  const model = String(modelId || "").toLowerCase().trim();
  if (model.includes("chat-latest")) {
    if (/^gpt-5/.test(model)) return 16384;
  }
  if (/^gpt-5\.5/.test(model)) return 128000;
  if (/^gpt-5/.test(model)) return 128000;
  if (/^(o1|o3|o4)/.test(model)) return 100000;
  if (/^gpt-4o/.test(model)) return 16384;
  if (/^gpt-4/.test(model)) return 8192;
  return 128000;
}

/** Suggested minimum Max Tokens so reasoning models return visible output. */
export function getModelRecommendedMinTokens(modelId: string): number {
  const model = String(modelId || "").toLowerCase().trim();
  if (model.includes("chat-latest")) return 4096;
  if (/^gpt-5\.5-pro/.test(model)) return 65536;
  if (/^gpt-5\.5/.test(model)) return 32768;
  if (/^gpt-5-(mini|nano)/.test(model)) return 8192;
  if (/^gpt-5/.test(model)) return 16384;
  if (/^(o1|o3|o4)/.test(model)) return 8192;
  return 4096;
}

export function getModelTokenHint(modelId: string): string {
  const model = String(modelId || "").toLowerCase().trim();
  if (/^gpt-5\.5/.test(model)) {
    return "GPT-5.5 uses max_completion_tokens for reasoning plus JSON output; 32K+ is recommended for large legal/EC documents.";
  }
  if (/^gpt-5/.test(model)) {
    return "GPT-5 uses tokens for internal reasoning before returning JSON.";
  }
  if (/^(o1|o3|o4)/.test(model)) {
    return "Reasoning models share the token budget between thinking and the JSON answer.";
  }
  return "Set high enough for long documents; the model limit is the completion token cap.";
}

/** Fields used by Global API & Models → Default Settings (section 3). */
export interface AiGlobalSettingsState {
  is_enabled: number;
  active_api_tool_id?: number;
  openai_model: string;
  max_retries: number;
  retry_delay_ms: number;
  max_tokens: number;
  temperature: number;
}

export function normalizePromptFromApi(
  row: Record<string, unknown>,
  globalSettings?: AiGlobalSettingsState | Record<string, unknown> | null,
): AiProcessPromptConfig {
  const gs = globalSettings || {};
  const processType = String(row.process_type || "");
  const defaultLabels: Record<string, string> = {
    process: "Process (Typing)",
    verification: "Verification",
    rectification: "Rectification",
  };
  const closeRemarks = normalizeCloseRemarkOptions(row.close_remark_options);

  return {
    id: row.id ? Number(row.id) : undefined,
    process_type: processType,
    process_label:
      String(row.process_label || "").trim() ||
      defaultLabels[processType] ||
      String(row.prompt_title || "").trim() ||
      processType,
    is_enabled: Number(row.is_enabled) ? 1 : 0,
    is_system: row.is_system != null ? (Number(row.is_system) ? 1 : 0) : 0,
    prompt_title: String(row.prompt_title || ""),
    system_prompt: String(row.system_prompt || ""),
    user_prompt: String(row.user_prompt || "").trim(),
    document_content_type:
      String(row.document_content_type || "").toLowerCase() === "pdf" ? "pdf" : "word",
    send_ec_document: Number(row.send_ec_document) ? 1 : 0,
    close_remark_options: closeRemarks,
    openai_model: String(row.openai_model || gs.openai_model || ""),
    max_tokens: Number(row.max_tokens) || Number(gs.max_tokens) || 0,
    temperature: Number(row.temperature) || Number(gs.temperature) || 0,
    max_retries: Number(row.max_retries) || Number(gs.max_retries) || 0,
    retry_delay_ms: Number(row.retry_delay_ms) || Number(gs.retry_delay_ms) || 0,
  };
}

/** Map API rows only — defaults are seeded in the database by prompts_get (PHP). */
export function mapPromptsFromApi(
  apiPrompts: unknown[],
  globalSettings?: AiGlobalSettingsState | Record<string, unknown> | null,
): AiProcessPromptConfig[] {
  return (apiPrompts || [])
    .filter((p): p is Record<string, unknown> => {
      if (!p || typeof p !== "object") return false;
      const key = String((p as Record<string, unknown>).process_type || "").trim().toLowerCase();
      return /^[a-z][a-z0-9_]{0,31}$/.test(key);
    })
    .map((p) => normalizePromptFromApi(p, globalSettings))
    .sort((a, b) => compareProcessTypes(a.process_type, b.process_type));
}

export function normalizeGlobalSettingsFromApi(data: Record<string, unknown> | null | undefined): AiGlobalSettingsState {
  const raw = data || {};
  return {
    is_enabled: Number(raw.is_enabled) ? 1 : 0,
    active_api_tool_id: Number(raw.active_api_tool_id) || 0,
    openai_model: String(raw.openai_model || "gpt-4o-mini"),
    max_retries: Number(raw.max_retries ?? 3),
    retry_delay_ms: Number(raw.retry_delay_ms ?? 2000),
    max_tokens: Number(raw.max_tokens ?? 4096),
    temperature: Number(raw.temperature ?? 0.2),
  };
}

export function normalizeApiToolFromApi(row: Record<string, unknown>): AiApiToolOption {
  return {
    id: row.id ? Number(row.id) : undefined,
    tool_label: String(row.tool_label || ""),
    api_endpoint_url: String(row.api_endpoint_url || ""),
    openai_api_key_masked: row.openai_api_key_masked ? String(row.openai_api_key_masked) : undefined,
    is_enabled: Number(row.is_enabled) ? 1 : 0,
  };
}

export function normalizeModelFromApi(row: Record<string, unknown>): AiModelOption {
  return {
    id: row.id ? Number(row.id) : undefined,
    model_id: String(row.model_id || ""),
    model_label: String(row.model_label || ""),
    is_enabled: Number(row.is_enabled) ? 1 : 0,
  };
}

export function buildSettingsSavePayload(
  settings: AiGlobalSettingsState,
  activeApiToolId: number,
  updatedBy?: number | string,
) {
  return {
    is_enabled: settings.is_enabled ? 1 : 0,
    active_api_tool_id: activeApiToolId > 0 ? activeApiToolId : undefined,
    openai_model: settings.openai_model || "gpt-4o-mini",
    max_retries: settings.max_retries ?? 3,
    retry_delay_ms: settings.retry_delay_ms ?? 2000,
    max_tokens: settings.max_tokens ?? 4096,
    temperature: settings.temperature ?? 0.2,
    updated_by: updatedBy,
  };
}

export function buildApiToolRowSavePayload(
  tool: AiApiToolOption,
  index: number,
  apiKeyInput: string,
): AiApiToolSaveRow {
  const payload: AiApiToolSaveRow = {
    tool_label: (tool.tool_label || "").trim(),
    api_endpoint_url: (tool.api_endpoint_url || "").trim(),
    is_enabled: tool.is_enabled ? 1 : 0,
  };
  if (tool.id) {
    payload.id = tool.id;
  }
  const key = (apiKeyInput || "").trim();
  if (key) {
    payload.openai_api_key = key;
  }
  if (!tool.id) {
    payload._client_key = tool._client_key || `idx_${index}`;
  }
  return payload;
}

export function isApiToolKeyRequired(tool: AiApiToolOption, apiKeyInput: string): boolean {
  if (isOllamaEndpoint(tool.api_endpoint_url || "")) {
    return false;
  }
  return !(apiKeyInput || "").trim() && !tool.openai_api_key_masked;
}

export function buildApiToolsSavePayload(
  tools: AiApiToolSaveRow[],
  activeApiToolId: number | undefined,
  activeClientKey: string | undefined,
  updatedBy?: number | string,
): {
  tools: AiApiToolSaveRow[];
  active_api_tool_id?: number;
  active_client_key?: string;
  updated_by?: number | string;
} {
  return {
    tools,
    ...(activeApiToolId ? { active_api_tool_id: activeApiToolId } : {}),
    ...(activeClientKey ? { active_client_key: activeClientKey } : {}),
    updated_by: updatedBy,
  };
}

export function buildModelsSavePayload(models: AiModelOption[], updatedBy?: number | string) {
  return {
    models: models
      .filter((m) => (m.model_id || "").trim() !== "")
      .map((m) => ({
        ...(m.id ? { id: m.id } : {}),
        model_id: (m.model_id || "").trim(),
        model_label: (m.model_label || m.model_id || "").trim(),
        is_enabled: m.is_enabled ? 1 : 0,
      })),
    updated_by: updatedBy,
  };
}

function apiToolRowKeyForSnapshot(tool: AiApiToolOption, index: number): string {
  return tool._client_key || (tool.id ? `id_${tool.id}` : `idx_${index}`);
}

/** Stable JSON snapshot for unsaved-change detection on the admin management page. */
export function buildAiVerificationAdminSnapshot(input: {
  settings: AiGlobalSettingsState | Record<string, unknown>;
  apiTools: AiApiToolOption[];
  models: AiModelOption[];
  prompts: AiProcessPromptConfig[];
  apiToolKeyInputs: Record<string, string>;
}): string {
  const { apiTools, models, prompts, apiToolKeyInputs } = input;
  const settings = normalizeGlobalSettingsFromApi(
    input.settings as Record<string, unknown> | null | undefined,
  );
  const labeledTools = apiTools.filter((t) => (t.tool_label || "").trim() !== "");
  const tools = labeledTools.map((t, i) => {
    const rowKey = apiToolRowKeyForSnapshot(t, i);
    const keyInput = (apiToolKeyInputs[rowKey] || "").trim();
    const row = buildApiToolRowSavePayload(t, i, keyInput);
    if (row.openai_api_key) {
      return { ...row, openai_api_key: `__changed__:${row.openai_api_key.length}` };
    }
    const { openai_api_key: _omit, ...rest } = row;
    return rest;
  });
  const enabledIndex = labeledTools.findIndex((t) => Number(t.is_enabled) === 1);
  const idx = enabledIndex >= 0 ? enabledIndex : 0;
  const enabledTool = labeledTools[idx];
  let activeTool: string | number = 0;
  if (enabledTool?.id) activeTool = enabledTool.id;
  else if (enabledTool?._client_key) activeTool = enabledTool._client_key;

  return JSON.stringify({
    settings: {
      is_enabled: settings.is_enabled ? 1 : 0,
      openai_model: settings.openai_model,
      max_retries: settings.max_retries,
      retry_delay_ms: settings.retry_delay_ms,
      max_tokens: settings.max_tokens,
      temperature: settings.temperature,
    },
    active_tool: activeTool,
    tools,
    models: buildModelsSavePayload(models).models,
    prompts: buildPromptsSavePayload(prompts).prompts,
  });
}

export function buildPromptsSavePayload(
  prompts: AiProcessPromptConfig[],
  updatedBy?: number | string,
): { prompts: AiProcessPromptSaveRow[]; updated_by?: number | string } {
  return {
    prompts: prompts.map((p) => {
      const closeRemarks = normalizeCloseRemarkOptions(p.close_remark_options);
      return {
      ...(p.id ? { id: p.id } : {}),
      process_type: (p.process_type || "").trim().toLowerCase(),
      process_label: p.process_label || p.process_type,
      is_enabled: p.is_enabled ? 1 : 0,
      is_system: p.is_system ? 1 : 0,
      prompt_title: p.prompt_title || p.process_label || p.process_type,
      system_prompt: p.system_prompt || "",
      user_prompt: (p.user_prompt || "").trim(),
      document_content_type: p.document_content_type === "pdf" ? "pdf" : "word",
      send_ec_document: p.send_ec_document ? 1 : 0,
      openai_model: p.openai_model || "",
      max_tokens: p.max_tokens ?? 0,
      temperature: p.temperature ?? 0,
      max_retries: p.max_retries ?? 0,
      retry_delay_ms: p.retry_delay_ms ?? 0,
      close_remark_options: closeRemarks.length
        ? closeRemarks
        : [...DEFAULT_CLOSE_REMARK_OPTIONS],
      };
    }),
    updated_by: updatedBy,
  };
}
